import React, { useState, useEffect } from "react";
import CustomSelect from "@/components/CustomSelect";

import { Icon } from "@cobasi/ds-icons";
import MapMarkerAlt from "@cobasi/ds-icons/solid/mapMarkerAlt";
import ChevronDown from "@cobasi/ds-icons/solid/chevronDown";

import CarouselFlex from "@/components/CarouselFlex";
import {
  chips,
  filterDataInitial,
  getCitiesOptions,
  getNeighborhoodsOptions,
  getStatesOptions
} from "@/utils/tsx/filters";
import styles from "./styles.module.scss";
import type {
  FilterDataProps,
  CityItem,
  NeighborhoodItem,
  StoreListItem
} from "@/types/modules/filters";
import classNames from "classnames";
import MaskedTextField from "../MaskedTextField";
import { useWindowSize } from "@/hooks/useWindowSize";
import { validateZipcode } from "@/utils/helpers";

export interface FiltersProps {
  filterData: FilterDataProps;
  // eslint-disable-next-line no-unused-vars
  onChange: (data: FilterDataProps) => void;
  onFindLocation: () => void;
  // eslint-disable-next-line no-unused-vars
  onFilter: (data: FilterDataProps) => void;
  storeListData: StoreListItem[];
}

export const Filters = ({
  filterData,
  onChange,
  onFilter,
  onFindLocation,
  storeListData
}: FiltersProps) => {
  const { state, city, neighborhood, zipcode, filterBy } = filterData;

  const { mobile } = useWindowSize();

  const statesOptions = getStatesOptions(storeListData);

  const [cityOptions, setCityOptions] = useState<CityItem[]>([]);
  const [neighborhoodOptions, setNeighborhoodOptions] = useState<
    NeighborhoodItem[]
  >([]);
  const [zipcodeError, setZipcodeError] = useState(false);

  useEffect(() => {
    const filteredCities = state ? getCitiesOptions(storeListData, state) : [];
    setCityOptions(filteredCities);
    setNeighborhoodOptions([]);
    onChange({ ...filterData, city: "", neighborhood: "" });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, storeListData]);

  useEffect(() => {
    if (state && city) {
      const filteredNeighborhoods = getNeighborhoodsOptions(
        storeListData,
        state,
        city
      );
      setNeighborhoodOptions(filteredNeighborhoods);
      onChange({ ...filterData, neighborhood: "" });
    } else {
      setNeighborhoodOptions([]);
      onChange({ ...filterData, neighborhood: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, storeListData]);

  const handleChange = (id: string, value: string) => {
    if (id === "filterBy") {
      return onChange({
        ...filterDataInitial,
        [id]: value as "zipcode" | "address"
      });
    }

    onChange({ ...filterData, [id]: value });
  };

  const handleChangeChip = (title: string) => {
    let newFilterData = { ...filterData };

    if (filterData.facilidades.includes(title)) {
      newFilterData = {
        ...filterData,
        facilidades: filterData.facilidades.filter(
          (facilidade) => facilidade !== title
        )
      };
    } else {
      newFilterData = {
        ...filterData,
        facilidades: [...filterData.facilidades, title]
      };
    }
    onChange(newFilterData);
  };

  const handleKeyDown = (e: any) => {
    if (zipcodeError) {
      setZipcodeError(false);
    }

    if (e.key === "Enter" && filterData.filterBy === "zipcode") {
      const zipcodeIsValid = validateZipcode(filterData.zipcode);
      setZipcodeError(!zipcodeIsValid);

      if (zipcodeIsValid) {
        onFilter(filterData);
      }
    }
  };

  return (
    <div className={styles.filters}>
      <div className={`${styles["filters-panel"]}`}>
        <MaskedTextField
          id="store-filter-zipcode-panel"
          name="zipcode"
          autoComplete="postal-code"
          type="text"
          value={zipcode}
          placeholder="Digite o CEP"
          onChange={(e) => handleChange("zipcode", e.target.value)}
          format="#####-###"
          fullWidth
          customSize="sm"
          onKeyDown={handleKeyDown}
          error={zipcodeError}
        />

        <button
          className={`${styles["btn-search"]} btn btn-md`}
          onClick={() => {
            onFilter({ ...filterData, filterBy: "zipcode" });
          }}
        >
          Buscar
        </button>
      </div>
      <div className={`${styles["filters-input"]}`}>
        <button
          onClick={onFindLocation}
          className={`${styles["btn-local"]} ${styles.btnAcessibility} btn btn-md btnArea outlined`}
        >
          <Icon icon={MapMarkerAlt} className={styles["icon-location"]} />
          Usar localização atual
        </button>
        <div className={`${styles["line-flex"]}`}>
          <hr className="divider" /> <span>ou</span> <hr className="divider" />
        </div>
        <div
          className={classNames(
            styles["fields-area"],
            filterBy === "zipcode" ? "form-zipcode" : "form-address"
          )}
        >
          <CustomSelect
            id="store-filter-by"
            name="filterBy"
            options={[
              { value: "zipcode", label: "CEP" },
              { value: "address", label: "Estado/Cidade" }
            ]}
            placeholder="Filtrar por"
            label="Filtrar por"
            value={filterBy}
            onChange={(e: any) => handleChange("filterBy", e.target.value)}
            icon={ChevronDown}
            className="select-filter-by body-text-sm"
          />

          <div className={classNames(styles["row-flex"], `group-form-address`)}>
            <CustomSelect
              id="store-filter-state"
              name="state"
              autoComplete="address-level1"
              options={statesOptions}
              placeholder="Estado"
              value={state}
              onChange={(e: any) => handleChange("state", e.target.value)}
              icon={ChevronDown}
              className="body-text-sm"
            />
            <CustomSelect
              id="store-filter-city"
              name="city"
              autoComplete="address-level2"
              options={cityOptions}
              placeholder="Cidade"
              value={city}
              onChange={(e): any => handleChange("city", e.target.value)}
              icon={ChevronDown}
              className="body-text-sm"
            />
            <CustomSelect
              id="store-filter-neighborhood"
              name="neighborhood"
              autoComplete="address-level3"
              options={neighborhoodOptions}
              placeholder="Bairro"
              value={neighborhood}
              onChange={(e: any) =>
                handleChange("neighborhood", e.target.value)
              }
              icon={ChevronDown}
              className="body-text-sm"
            />
          </div>
          <div className={classNames(styles["row-flex"], `group-form-zipcode`)}>
            <MaskedTextField
              id="store-filter-zipcode"
              name="zipcode"
              autoComplete="postal-code"
              type="text"
              value={zipcode}
              placeholder="Digite o CEP"
              onChange={(e) => handleChange("zipcode", e.target.value)}
              format="#####-###"
              fullWidth
              customSize="sm"
              onKeyDown={handleKeyDown}
              error={zipcodeError}
            />
          </div>
          <button
            onClick={() => {
              onFilter({
                ...filterData,
                filterBy: mobile ? "address" : filterBy,
                zipcode: mobile ? "" : zipcode
              });
            }}
            className={`${styles["search-button"]} btn btn-md`}
          >
            Buscar
          </button>
        </div>
        <div className={`${styles["filter-container"]}`}>
          <span className={styles.title}>Filtrar por:</span>
          <div className={styles["filter-carousel"]}>
            <CarouselFlex showArrows={false} classNameArrow="arrow">
              {chips.map((chip, index) => {
                const isSelected = filterData.facilidades.includes(chip.title);

                return (
                  <div className={styles.chip} key={index}>
                    <button
                      onClick={() => handleChangeChip(chip.title)}
                      className={`btn chip-filter chip-lemon outline ${isSelected ? "selected" : ""}`}
                    >
                      <span className={styles["chip-filter-icon"]}>
                        {chip.icon}
                      </span>
                      <span className="chip-filter-label">{chip.title}</span>
                    </button>
                  </div>
                );
              })}
            </CarouselFlex>
          </div>
        </div>
      </div>
    </div>
  );
};
